/**
 * The VAT return, as filed: quarterly by default.
 *
 * Laid out like the ZATCA form -- box number, amount, adjustment, VAT --
 * so the accountant can copy it across line by line. Each box opens the
 * vouchers behind it, because the first question about any figure on a
 * return is "which invoices make this?".
 *
 * Boxes BizVision cannot fill yet (imports, zero-rated vs exempt) are said
 * to be so rather than shown as a confident zero.
 */
import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Alert, Box, Collapse, IconButton, MenuItem, Skeleton, Stack, TextField,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useTranslation } from 'react-i18next'

import { fetchVatReturn } from '../api/endpoints'
import type { VatBox } from '../api/types'
import { c, fmtMoney } from '../theme'
import ReportShell, { rangeLabel } from './ReportShell'
import ReportFooter from './ReportFooter'
import ReportToolbar from './ReportToolbar'
import VoucherDrawer from './VoucherDrawer'
import { useDateRange } from './useDateRange'
import { downloadCsv } from './csv'
import { gregorian } from './hijri'

/** The quarter a date falls in, 1-4. */
export function quarterOf(d: Date): number {
  return Math.floor(d.getMonth() / 3) + 1
}

export default function VatReturn() {
  const { t, i18n } = useTranslation()
  const { range, setRange, presets } = useDateRange()
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [quarter, setQuarter] = useState(quarterOf(now))
  const [openBox, setOpenBox] = useState<string | null>(null)
  const [voucher, setVoucher] = useState<number | null>(null)

  const q = useQuery({
    queryKey: ['vat-return', year, quarter],
    queryFn: () => fetchVatReturn({ year, quarter }),
  })
  const v = q.data
  const years = [now.getFullYear() - 2, now.getFullYear() - 1, now.getFullYear()]
  const name = (key: string) => t(`vat.box.${key}`)

  const row = (b: VatBox, css?: string) => (
    <Fragment key={b.key}>
      <tr className={css ?? (b.docs.length ? 'clickable' : '')}
        onClick={() => b.docs.length
          && setOpenBox(openBox === b.key ? null : b.key)}>
        <td className="num" style={{ textAlign: 'start', width: 56 }}>{b.no}</td>
        <td>
          {name(b.key)}
          {b.docs.length > 0 && (
            <IconButton size="small" aria-label={t('vat.showDocs')}
              sx={{ ml: 0.5, transform: openBox === b.key ? 'rotate(180deg)' : '' }}>
              <ExpandMoreIcon fontSize="small" />
            </IconButton>
          )}
        </td>
        <td className="num">{fmtMoney(b.amount)}</td>
        <td className="num">{fmtMoney(b.adjustment)}</td>
        <td className="num">{fmtMoney(b.vat)}</td>
      </tr>
      {b.docs.length > 0 && (
        <tr>
          <td colSpan={5} style={{ padding: 0, border: 0 }}>
            <Collapse in={openBox === b.key} unmountOnExit>
              <Box sx={{ px: 2, py: 1, bgcolor: c.surfacePage }}>
                <table className="rpt">
                  <tbody>
                    {b.docs.map((d) => (
                      <tr key={d.voucher_id} className="clickable"
                        onClick={() => setVoucher(d.voucher_id)}>
                        <td className="num" style={{ textAlign: 'start', width: 104 }}>
                          {gregorian(d.date, i18n.language)}</td>
                        <td className="num" style={{ textAlign: 'start', width: 140,
                          color: 'var(--ink-muted)', fontSize: '0.78rem' }}>{d.voucher_no}</td>
                        <td>{d.party_name ?? '—'}</td>
                        <td className="num" style={{ textAlign: 'start',
                          fontSize: '0.78rem', color: 'var(--ink-muted)' }}>
                          {d.party_tax_number ?? ''}</td>
                        <td className="num">{fmtMoney(d.amount)}</td>
                        <td className="num">{fmtMoney(d.vat)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Collapse>
          </td>
        </tr>
      )}
    </Fragment>
  )

  return (
    <>
      <ReportShell titleKey="nav.vatReturn"
        period={v ? rangeLabel(v.from_date, v.to_date, i18n.language, t) : undefined}
        toolbar={
          <ReportToolbar range={range} setRange={setRange} presets={presets}
            hideDates
            pdf={{ path: 'vat-return.pdf',
                   params: { year: String(year), quarter: String(quarter) },
                   filename: `vat-return-${year}-Q${quarter}.pdf` }}
            onExport={v ? () => downloadCsv(`vat-return-${year}-Q${quarter}`, [
              ['Box', 'Item', 'Amount', 'Adjustment', 'VAT'],
              ...[...v.sales, v.total_sales, ...v.purchases, v.total_purchases]
                .map((b) => [b.no, name(b.key), b.amount, b.adjustment, b.vat]),
              ['13', t('vat.due'), '', '', v.vat_due],
            ]) : undefined}>
            <TextField select size="small" label={t('vat.year')} value={year}
              onChange={(e) => setYear(Number(e.target.value))} sx={{ width: 110 }}>
              {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
            <TextField select size="small" label={t('vat.quarter')} value={quarter}
              onChange={(e) => setQuarter(Number(e.target.value))} sx={{ width: 170 }}>
              {[1, 2, 3, 4].map((n) => (
                <MenuItem key={n} value={n}>{t(`vat.q${n}`)}</MenuItem>
              ))}
            </TextField>
          </ReportToolbar>
        }>

        {q.isLoading || !v ? (
          <Box sx={{ p: 2 }}><Skeleton /><Skeleton /><Skeleton /></Box>
        ) : (
          <Box sx={{ px: 2, pb: 1 }}>
            <Stack direction="row" spacing={3} sx={{ py: 1.5 }}>
              <Box>
                <div className="label">{t('vat.due')}</div>
                <span className="num num-lg" style={{ fontWeight: 600,
                  color: Number(v.vat_due) < 0 ? c.primaryInk : undefined }}>
                  {fmtMoney(v.vat_due)}
                </span>
                <Typography variant="caption" sx={{ display: 'block', color: c.inkMuted }}>
                  {Number(v.vat_due) < 0 ? t('vat.refund') : t('vat.payable')}
                </Typography>
              </Box>
            </Stack>
            <table className="rpt">
              <thead>
                <tr>
                  <th>{t('vat.boxNo')}</th>
                  <th>{t('vat.salesHead')}</th>
                  <th className="num" style={{ width: 140 }}>{t('vat.amount')}</th>
                  <th className="num" style={{ width: 140 }}>{t('vat.adjustment')}</th>
                  <th className="num" style={{ width: 140 }}>{t('vat.vat')}</th>
                </tr>
              </thead>
              <tbody>
                {v.sales.map((b) => row(b))}
                {row(v.total_sales, 'sub')}
              </tbody>
              <thead>
                <tr>
                  <th />
                  <th>{t('vat.purchasesHead')}</th>
                  <th /><th /><th />
                </tr>
              </thead>
              <tbody>
                {v.purchases.map((b) => row(b))}
                {row(v.total_purchases, 'sub')}
              </tbody>
              <tfoot>
                <tr>
                  <td>13</td>
                  <td>{t('vat.due')}</td>
                  <td /><td />
                  <td className="num">{fmtMoney(v.vat_due)}</td>
                </tr>
              </tfoot>
            </table>
            <Alert severity="info" sx={{ mt: 1.5 }}>{t('vat.limits')}</Alert>
          </Box>
        )}
        <ReportFooter />
      </ReportShell>
      <VoucherDrawer voucherId={voucher} onClose={() => setVoucher(null)} />
    </>
  )
}
